import { activePrivateLessonSharedMembershipLabel } from "@/modules/private-lessons/domain/lib/active-private-lesson-package-display";
import { useCancelPrivateLessonPackage } from "@/modules/private-lessons/presentation/api/queries";
import { PrivateLessonGroupPackageBadge } from "@/modules/private-lessons/presentation/ui/PrivateLessonGroupPackageBadge";
import { formatDate } from "@/shared/lib/date-time";
import { CancelPackageDialog } from "@/shared/ui/CancelPackageDialog";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { RadialChart, type RadialChartData } from "@/shared/ui/RadialChart";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Progress,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  CalendarDaysIcon,
  CalendarIcon,
  FileTextIcon,
  InfoIcon,
  PackageIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import type { ActivePrivateLessonPackageDTO } from "../../domain/repo/active-package.dto";
import { studentsKeys } from "../api/queries";

type ActivePrivateLessonPackageSectionProps = {
  studentId: string;
  packages: NonNullable<ActivePrivateLessonPackageDTO>[];
};

export function ActivePrivateLessonPackageSection({
  studentId: _studentId,
  packages,
}: ActivePrivateLessonPackageSectionProps) {
  const nav = useNavigate();
  const qc = useQueryClient();

  if (packages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileTextIcon className="size-5 text-destructive" />
              <CardTitle>ແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານ</CardTitle>
            </div>
            <Button
              onClick={() => {
                nav({
                  to: "/app/sell",
                  search: { studentId: _studentId },
                });
              }}
            >
              <ShoppingCartIcon className="size-4" />
              ຂາຍແພັກເກດຮຽນສ່ວນຕົວໃໝ່
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <PackageIcon className="size-12 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ບໍ່ມີແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານ</EmptyTitle>
              <EmptyDescription>
                ນັກຮຽນຍັງບໍ່ມີແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານຢູ່ ກະລຸນາຂາຍແພັກເກດຮຽນສ່ວນຕົວໃໝ່ໃຫ້ນັກຮຽນ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileTextIcon className="size-5 text-destructive" />
              <CardTitle>ແພັກເກດຮຽນສ່ວນຕົວທີ່ໃຊ້ງານ ({packages.length})</CardTitle>
            </div>
            <Button
              onClick={() => {
                nav({
                  to: "/app/sell",
                  search: { studentId: _studentId },
                });
              }}
            >
              <ShoppingCartIcon className="size-4" />
              ຂາຍແພັກເກດຮຽນສ່ວນຕົວໃໝ່
            </Button>
          </div>
        </CardHeader>
      </Card>

      {packages.map((activePackage) => (
        <ActivePrivateLessonPackageCard
          key={activePackage.id}
          studentId={_studentId}
          activePackage={activePackage}
          onCancelled={() => {
            qc.invalidateQueries({ queryKey: studentsKeys.detail(_studentId) });
          }}
        />
      ))}
    </div>
  );
}

function ActivePrivateLessonPackageCard({
  studentId: _studentId,
  activePackage,
  onCancelled,
}: {
  studentId: string;
  activePackage: NonNullable<ActivePrivateLessonPackageDTO>;
  onCancelled: () => void;
}) {
  const nav = useNavigate();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const cancelPackage = useCancelPrivateLessonPackage();

  const usedHours = activePackage.usedHours;
  const totalHours = activePackage.totalHours;
  const remainingHours = activePackage.remainingHours;
  const usedPercentage =
    totalHours > 0 ? Math.round((usedHours / totalHours) * 100) : 0;

  // Chart data for RadialChart
  const chartData: RadialChartData[] = [
    { name: "used", value: usedHours },
    { name: "remaining", value: remainingHours },
  ];

  const chartConfig: ChartConfig = {
    used: {
      label: "Used",
      color: "var(--destructive)",
    },
    remaining: {
      label: "Remaining",
      color: "var(--muted)",
    },
  };

  return (
    <div className="space-y-6">
      <CancelPackageDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        packageLabel={activePackage.templateName}
        onConfirm={async (cancelReason) => {
          await cancelPackage.mutateAsync({
            id: activePackage.id,
            cancelReason,
          });
          onCancelled();
        }}
      />

      {/* Two Panels: Hour Usage and Package Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left Panel: Hour Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-sm uppercase">
              ການໃຊ້ງານຊົ່ວໂມງ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Radial Chart */}
            <div className="flex justify-center">
              <RadialChart
                data={chartData}
                config={chartConfig}
                centerValue={remainingHours}
                centerLabel="ເຫຼືອ"
                className="mx-auto aspect-square max-h-[250px]"
                startAngle={90}
                endAngle={450}
              />
            </div>

            {/* Usage Summary */}
            <div className="space-y-2">
              <div className="text-center text-sm">
                <span className="font-semibold">
                  ໃຊ້ແລ້ວ: {usedHours.toFixed(1)} ຊົ່ວໂມງ
                </span>{" "}
                <span className="text-muted-foreground">
                  ທັງໝົດ: {totalHours} ຊົ່ວໂມງ
                </span>
              </div>

              {/* Progress Bar */}
              <Progress value={usedPercentage} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Right Panel: Package Details */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg">
                {activePackage.templateName}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <PrivateLessonGroupPackageBadge
                  packageKind={activePackage.packageKind}
                  groupSize={activePackage.groupSize}
                />
                <Badge variant="default">ໃຊ້ງານ</Badge>
              </div>
            </div>
            {activePrivateLessonSharedMembershipLabel(activePackage) && (
              <p className="text-muted-foreground text-sm">
                {activePrivateLessonSharedMembershipLabel(activePackage)}
              </p>
            )}
            <p className="text-muted-foreground text-sm">
              Package ID: #{activePackage.id.slice(0, 8).toUpperCase()}
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  nav({
                    to: "/app/private-lessons/packages/$packageId",
                    params: { packageId: activePackage.id },
                  });
                }}
              >
                <InfoIcon className="size-4" />
                ເບິ່ງລາຍລະອຽດ
              </Button>
              {activePackage.status === "active" &&
                activePackage.membershipRole === "owner" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XIcon className="size-4" />
                    ຍົກເລີກແພັກເກດ
                  </Button>
                )}
              {activePackage.status === "active" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    nav({
                      to: "/app/private-lessons/bookings/create",
                      search: {
                        studentId: _studentId,
                        packageId: activePackage.id,
                      },
                    });
                  }}
                >
                  <CalendarDaysIcon className="size-4" />
                  ຈອງຮຽນ
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີເລີ່ມຕົ້ນ
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.startDate)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີໝົດອາຍຸ
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.expireDate)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີຊື້
                  </div>
                  <div className="flex items-center gap-2">
                    <ShoppingBagIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.purchasedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Alert */}
            {/* <div className="mt-6 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-3">
                <InfoIcon className="mt-0.5 size-5 text-destructive" />
                <div className="flex-1">
                  <p className="text-sm">
                    ແພັກເກດນີ້ມີສິດໄດ້ຮັບສ່ວນຫຼຸດ 10% ສຳລັບການຕໍ່ອາຍຸກ່ອນໝົດອາຍຸ.
                  </p>
                  <Button
                    variant="link"
                    className="mt-2 h-auto p-0 text-destructive"
                    onClick={() => {
                      // TODO: Navigate to package details
                    }}
                  >
                    ເບິ່ງລາຍລະອຽດ
                  </Button>
                </div>
              </div>
            </div> */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
